import React, { useState } from 'react';
import { App, Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Space, Switch, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import inventoryService, { type UomUnit } from '@/services/inventory.service';

const { Title, Text } = Typography;

const QUERY_KEY = ['inventory', 'uom-units', 'all'] as const;

export const UomMasterPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<UomUnit | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => inventoryService.getUomUnits({ all: true }),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, sort_order: 100 });
    setOpen(true);
  };

  const openEdit = (row: UomUnit) => {
    setEditing(row);
    form.setFieldsValue({
      code: row.code,
      name: row.name,
      description: row.description ?? undefined,
      category: row.category ?? undefined,
      sort_order: row.sort_order,
      is_active: row.is_active !== false,
    });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        await inventoryService.updateUomUnit(editing.id, {
          name: String(values.name).trim(),
          description: values.description ? String(values.description).trim() : null,
          category: values.category ? String(values.category).trim() : null,
          sort_order: Number(values.sort_order) || 0,
          is_active: !!values.is_active,
        });
        message.success('UOM updated');
      } else {
        await inventoryService.createUomUnit({
          code: String(values.code).trim(),
          name: String(values.name).trim(),
          description: values.description ? String(values.description).trim() : undefined,
          category: values.category ? String(values.category).trim() : undefined,
          sort_order: Number(values.sort_order) || 100,
          is_active: values.is_active !== false,
        });
        message.success('UOM created');
      }
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['inventory', 'uom-units'] });
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || error?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await inventoryService.deleteUomUnit(id);
      message.success('UOM deleted');
      await queryClient.invalidateQueries({ queryKey: ['inventory', 'uom-units'] });
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || 'Delete failed');
    }
  };

  const columns: ColumnsType<UomUnit> = [
    { title: 'Code', dataIndex: 'code', key: 'code', width: 100 },
    { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true },
    { title: 'Category', dataIndex: 'category', key: 'category', width: 120, render: (v) => v || '—' },
    { title: 'Sort', dataIndex: 'sort_order', key: 'sort_order', width: 72 },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (v: boolean) => (v !== false ? <Text type="success">Active</Text> : <Text type="secondary">Inactive</Text>),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this UOM?"
            description="Ensure no transactions rely on this code before deleting."
            onConfirm={() => handleDelete(row.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Title level={3} style={{ margin: 0 }}>
              Units of measure (UOM)
            </Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New UOM
            </Button>
          </Space>
          <Text type="secondary">
            Manufacturing and supply-chain units (PCS, KG, M, HR, etc.) are seeded automatically the first time you open this
            screen or any UOM list. Use this page to add custom codes or deactivate unused ones.
          </Text>
          <Table<UomUnit>
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={rows}
            pagination={{ defaultPageSize: 25, showSizeChanger: true }}
          />
        </Space>
      </Card>

      <Modal
        title={editing ? 'Edit UOM' : 'New UOM'}
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            name="code"
            label="Code"
            rules={[{ required: !editing, message: 'Code is required' }, { max: 20, message: 'Max 20 characters' }]}
          >
            <Input placeholder="e.g. PCS" disabled={!!editing} maxLength={20} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="Display name" />
          </Form.Item>
          <Form.Item name="category" label="Category (optional)">
            <Input placeholder="e.g. Count, Length, Mass" />
          </Form.Item>
          <Form.Item name="sort_order" label="Sort order">
            <InputNumber min={0} max={99999} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Optional" />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
